import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { BaseButton, CloseButton, Checkbox, useAuth, useT } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

const FilterModal = ({
  onClose,
  coords,
  onApply,
  initialSort,
  initialChecked,
  initialAnsweredChecked,
  initialUnansweredChecked,
}) => {
  const t = useT();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sortBy, setSortBy] = useState(initialSort);
  const [checked, setChecked] = useState(initialChecked);
  const [unansweredChecked, setUnansweredChecked] = useState(
    initialUnansweredChecked,
  );

  const [dropdownCoords, setDropdownCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const sortButtonRef = useRef(null);

  const sortOptions = ['Newest', 'Oldest', 'Most Popular'];
  const sortOptionLabelsKr = {
    Newest: '최신순',
    Oldest: '오래된순',
    'Most Popular': '인기순',
  };

  const [isSortTouched, setIsSortTouched] = useState(false);
  const isFilterChanged =
    isSortTouched ||
    checked !== initialChecked ||
    unansweredChecked !== initialUnansweredChecked;

  const userRole = user?.role ?? null;
  const isAdmin = userRole === 'A';

  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false);
      onClose();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onClose]);

  useEffect(() => {
    setSortBy(initialSort);
    setChecked(initialChecked);
    setUnansweredChecked(initialUnansweredChecked);
  }, [initialSort, initialChecked, initialUnansweredChecked]);

  const handleApplyFilters = () => {
    onApply({ sortBy, checked, unansweredChecked });
    onClose();
  };

  const handleSortOptionClick = (option) => {
    setSortBy(option);
    setIsSortTouched(true);
    setIsOpen(false);
  };

  const handleSortButtonClick = () => {
    if (!isOpen && sortButtonRef.current) {
      const rect = sortButtonRef.current.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen((prev) => !prev);
  };

  return ReactDOM.createPortal(
    <div
      className='filter-modal-container fixed mt-1 z-[9999]'
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        width: '340px',
      }}
    >
      {/* 모달 내용 컨테이너 */}
      <div className='soft-70 bg-grayscale-gray00 border-grayscale-gray02 border-[1.6px] rounded-[30px] w-[340px] p-6 pb-[30px] shadow-popup-01'>
        {/* 헤더 */}
        <div className='flex justify-between items-center mb-[18px]'>
          <div className='typo-title2 text-text-primary'>
            {t('Filters', '필터')}
          </div>
          <CloseButton type='default' onClick={onClose} size='w-8 h-8' />
        </div>

        {/* Sort by 섹션 */}
        <div className='pb-[14px]'>
          <div className='typo-b14Strong text-text-secondary mb-3'>
            {t('Sort by', '정렬 기준')}
          </div>

          <div className='relative'>
            <button
              ref={sortButtonRef}
              onClick={handleSortButtonClick}
              className={`
                w-full h-12 p-3 pl-4 flex justify-between items-center
                rounded-[8px] typo-16
                border
                active:border-grayscale-gray07
                ${
                  isOpen || isSortTouched
                    ? 'border-grayscale-gray07'
                    : 'border-grayscale-gray03'
                }
              `}
            >
              {t(sortBy, sortOptionLabelsKr[sortBy] || sortBy)}
              {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            </button>

            {isOpen &&
              ReactDOM.createPortal(
                <ul
                  className='filter-modal-container absolute z-[9999] w-full p-2 mt-1 bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] shadow-popup-04'
                  style={{
                    position: 'absolute',
                    top: `${dropdownCoords.top}px`,
                    left: `${dropdownCoords.left}px`,
                    width: `${dropdownCoords.width}px`,
                  }}
                >
                  {sortOptions.map((option) => (
                    <li key={option} className='h-[38px] flex items-center'>
                      <BaseButton
                        type='text'
                        width='100%'
                        bgclass='bg-grayscale-gray00 border-none h-full min-h-[38px] p-0'
                        textclass='justify-start h-full'
                        hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
                        activeclass='active:bg-grayscale-gray01 active:text-text-primary'
                        icon={
                          <CheckIcon
                            className={`ml-2 transition-opacity ${
                              sortBy === option ? 'opacity-100' : 'opacity-0'
                            }`}
                          />
                        }
                        leftalign
                        onClick={() => handleSortOptionClick(option)}
                      >
                        {t(option, sortOptionLabelsKr[option] || option)}
                      </BaseButton>
                    </li>
                  ))}
                </ul>,
                document.body,
              )}
          </div>
        </div>

        {/* Checkbox 옵션 */}
        {isAdmin && (
          <div>
            <Checkbox
              checked={unansweredChecked}
              onChange={() => setUnansweredChecked(!unansweredChecked)}
              label={t('Unanswered only', '답변 없는 질문만')}
              className={`${unansweredChecked ? 'text-text-primary' : ''}`}
              boxClassName={
                unansweredChecked
                  ? 'bg-functional-success03 border-none'
                  : 'bg-grayscale-gray00 border border-grayscale-gray03'
              }
            />
          </div>
        )}

        <div className='py-[6px] pb-[18px]'>
          <Checkbox
            checked={checked}
            onChange={() => setChecked(!checked)}
            label={t('Only with tutor’s answer', '튜터 답변이 있는 질문만')}
            className={`${checked ? 'text-text-primary' : ''}`}
            boxClassName={
              checked
                ? 'bg-functional-success03 border-none'
                : 'bg-grayscale-gray00 border border-grayscale-gray03'
            }
          />
        </div>

        {/* 필터 적용 버튼 */}
        <BaseButton
          onClick={handleApplyFilters}
          width='100%'
          hideIcon
          isDisabled={!isFilterChanged}
        >
          {t('Select filters to apply', '적용할 필터를 선택하세요')}
        </BaseButton>
      </div>
    </div>,
    document.body,
  );
};

export default FilterModal;
